"use client";

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { MatchResult, ModeToggle } from "../base-match-template";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const parseOptionalInt = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  if (value.trim() === "") return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseOptionalNumber = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export type CricketScoreEditorProps = {
  initialData?: Record<string, unknown> | null;
  onChange: (data: Record<string, unknown>) => void;
};

export function CricketScoreEditor({
  initialData,
  onChange,
}: CricketScoreEditorProps) {
  const initialYourInnings = isRecord(initialData?.yourInnings)
    ? (initialData?.yourInnings as Record<string, unknown>)
    : null;
  const initialOpponentInnings = isRecord(initialData?.opponentInnings)
    ? (initialData?.opponentInnings as Record<string, unknown>)
    : null;

  const [yourInnings, setYourInnings] = useState(() => ({
    runs: parseOptionalInt(initialYourInnings?.runs) ?? 0,
    wickets: Math.min(10, parseOptionalInt(initialYourInnings?.wickets) ?? 0),
    overs: (() => {
      const parsed = parseOptionalNumber(initialYourInnings?.overs);
      return parsed != null && parsed >= 0 ? parsed : undefined;
    })(),
  }));
  const [opponentInnings, setOpponentInnings] = useState(() => ({
    runs: parseOptionalInt(initialOpponentInnings?.runs) ?? 0,
    wickets: Math.min(
      10,
      parseOptionalInt(initialOpponentInnings?.wickets) ?? 0,
    ),
    overs: (() => {
      const parsed = parseOptionalNumber(initialOpponentInnings?.overs);
      return parsed != null && parsed >= 0 ? parsed : undefined;
    })(),
  }));

  const initialChasingTeam =
    initialData?.chasingTeam === "your-team" ||
    initialData?.chasingTeam === "opponent-team"
      ? (initialData.chasingTeam as "your-team" | "opponent-team")
      : null;

  const [showDetails, setShowDetails] = useState(
    Boolean(
      initialChasingTeam ||
        typeof yourInnings.overs === "number" ||
        typeof opponentInnings.overs === "number",
    ),
  );
  const [chasingTeam, setChasingTeam] = useState<
    "your-team" | "opponent-team" | null
  >(initialChasingTeam);

  const result = useMemo(() => {
    if (yourInnings.runs === 0 && opponentInnings.runs === 0) return null;
    if (yourInnings.runs === opponentInnings.runs) return "tie" as const;

    const team1Won = yourInnings.runs > opponentInnings.runs;
    const team2Won = opponentInnings.runs > yourInnings.runs;

    const effectiveChasingTeam = showDetails ? chasingTeam : null;

    if (!effectiveChasingTeam) {
      if (team1Won) return "won-by-runs" as const;
      if (team2Won) return "loss" as const;
      return "tie" as const;
    }

    const winnerIsChasing =
      (effectiveChasingTeam === "your-team" && team1Won) ||
      (effectiveChasingTeam === "opponent-team" && team2Won);

    if (winnerIsChasing) return "won-by-wickets" as const;
    return team1Won ? ("won-by-runs" as const) : ("loss" as const);
  }, [yourInnings.runs, opponentInnings.runs, chasingTeam, showDetails]);

  useEffect(() => {
    onChange({
      yourInnings,
      opponentInnings,
      result,
      chasingTeam: showDetails ? chasingTeam : null,
    });
  }, [
    yourInnings,
    opponentInnings,
    result,
    chasingTeam,
    showDetails,
    onChange,
  ]);

  const yourWon = yourInnings.runs > opponentInnings.runs;
  const opponentWon = opponentInnings.runs > yourInnings.runs;

  return (
    <div className="space-y-3">
      <div className="space-y-3">
        <Label className="text-text-primary">Team 1 Score</Label>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Label className="text-xs text-text-secondary">Runs</Label>
            <Input
              type="number"
              value={yourInnings.runs || ""}
              onChange={(e) =>
                setYourInnings((prev) => ({
                  ...prev,
                  runs: Number(e.target.value) || 0,
                }))
              }
              className={cn(
                "mt-1 text-center text-lg font-bold",
                yourWon && "border-brand bg-brand/10",
              )}
              placeholder="0"
            />
          </div>
          <span className="text-xl text-text-secondary">/</span>
          <div className="w-20">
            <Label className="text-xs text-text-secondary">Wickets</Label>
            <Input
              type="number"
              value={yourInnings.wickets || ""}
              onChange={(e) =>
                setYourInnings((prev) => ({
                  ...prev,
                  wickets: Math.min(10, Number(e.target.value) || 0),
                }))
              }
              className="mt-1 text-center"
              placeholder="0"
              max={10}
            />
          </div>
        </div>

        {showDetails && (
          <div className="mt-2">
            <Label className="text-xs text-text-secondary">
              Overs (optional)
            </Label>
            <Input
              type="number"
              inputMode="decimal"
              value={
                typeof yourInnings.overs === "number" ? yourInnings.overs : ""
              }
              onChange={(e) =>
                setYourInnings((prev) => ({
                  ...prev,
                  overs:
                    e.target.value === "" ? undefined : Number(e.target.value),
                }))
              }
              className="mt-1 w-28 text-center"
              placeholder="0.0"
              min={0}
              max={999}
              step="0.1"
            />
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Label className="text-text-primary">Team 2 Score</Label>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Label className="text-xs text-text-secondary">Runs</Label>
            <Input
              type="number"
              value={opponentInnings.runs || ""}
              onChange={(e) =>
                setOpponentInnings((prev) => ({
                  ...prev,
                  runs: Number(e.target.value) || 0,
                }))
              }
              className={cn(
                "mt-1 text-center text-lg font-bold",
                opponentWon && "border-error bg-error/10",
              )}
              placeholder="0"
            />
          </div>
          <span className="text-xl text-text-secondary">/</span>
          <div className="w-20">
            <Label className="text-xs text-text-secondary">Wickets</Label>
            <Input
              type="number"
              value={opponentInnings.wickets || ""}
              onChange={(e) =>
                setOpponentInnings((prev) => ({
                  ...prev,
                  wickets: Math.min(10, Number(e.target.value) || 0),
                }))
              }
              className="mt-1 text-center"
              placeholder="0"
              max={10}
            />
          </div>
        </div>

        {showDetails && (
          <div className="mt-2">
            <Label className="text-xs text-text-secondary">
              Overs (optional)
            </Label>
            <Input
              type="number"
              inputMode="decimal"
              value={
                typeof opponentInnings.overs === "number"
                  ? opponentInnings.overs
                  : ""
              }
              onChange={(e) =>
                setOpponentInnings((prev) => ({
                  ...prev,
                  overs:
                    e.target.value === "" ? undefined : Number(e.target.value),
                }))
              }
              className="mt-1 w-28 text-center"
              placeholder="0.0"
              min={0}
              max={999}
              step="0.1"
            />
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-text-primary">Details</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowDetails((prev) => !prev)}
          >
            {showDetails ? "Hide" : "Add"}
          </Button>
        </div>

        {showDetails && (
          <ModeToggle
            label="Chasing Team"
            value={chasingTeam ?? "unknown"}
            onChange={(value) =>
              setChasingTeam(
                value === "your-team" || value === "opponent-team"
                  ? value
                  : null,
              )
            }
            options={[
              { value: "unknown", label: "Not sure" },
              { value: "your-team", label: "Team 1 chased" },
              { value: "opponent-team", label: "Team 2 chased" },
            ]}
          />
        )}
      </div>

      <MatchResult
        yourScore={yourInnings.runs}
        opponentScore={opponentInnings.runs}
      />
    </div>
  );
}
